import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { taskService } from '@services';

export const useTasks = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['tasks', params],
    queryFn: () => taskService.getAll(params),
    enabled: options.enabled !== undefined ? options.enabled : !!params.projectId,
    ...options,
  });
};

export const useTask = (id) => {
  return useQuery({
    queryKey: ['task', id],
    queryFn: () => taskService.getById(id),
    enabled: !!id,
  });
};

export const useCreateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: taskService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => taskService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task', id] });
    },
  });
};

export const useUpdateTaskStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, order }) => taskService.updateStatus(id, status, order),
    onMutate: async ({ id, status }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['tasks'] });

      // Snapshot the previous value
      const previousTasks = queryClient.getQueryData(['tasks']);

      // Optimistically update to the new value
      queryClient.setQueriesData({ queryKey: ['tasks'] }, (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((task) =>
            task.id === id ? { ...task, status } : task
          ),
        };
      });

      return { previousTasks };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks'], context.previousTasks);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: taskService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useReorderTasks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tasks }) => taskService.reorder(tasks),
    onMutate: async ({ tasks, allTasks }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks'] });

      const previousTasks = queryClient.getQueryData(['tasks']);

      // Optimistically update task order
      queryClient.setQueriesData({ queryKey: ['tasks'] }, (old) => {
        if (!old?.data) return old;

        const orderMap = new Map(tasks.map((t) => [t.id, t.order]));

        return {
          ...old,
          data: old.data.map((task) =>
            orderMap.has(task.id) ? { ...task, order: orderMap.get(task.id) } : task
          ),
        };
      });

      return { previousTasks };
    },
    onError: (err, variables, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks'], context.previousTasks);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// Attachment hooks
export const useTaskAttachments = (taskId, options = {}) => {
  return useQuery({
    queryKey: ['taskAttachments', taskId],
    queryFn: () => taskService.getAttachments(taskId),
    enabled: !!taskId,
    ...options,
  });
};

export const useAddTaskAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, file }) => taskService.addAttachment(taskId, file),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['taskAttachments', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
    },
  });
};

export const useAddTaskAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, files }) => taskService.addAttachments(taskId, files),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ['taskAttachments', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
    },
  });
};

export const useDeleteTaskAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attachmentId) => taskService.deleteAttachment(attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskAttachments'] });
      queryClient.invalidateQueries({ queryKey: ['task'] });
    },
  });
};
